import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCircle, Phone, MapPin, Heart, LogOut, CheckCircle, Loader2, User, Mail, Calendar, Users } from 'lucide-react';
interface FloatingLabelInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  icon?: React.ReactNode;
}
const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  icon
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;
  return <div className="relative" data-id="element-4544">
      <input type={type} value={value} onChange={onChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} required={required} className="w-full px-4 py-3.5 pt-6 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 outline-none" placeholder=" " data-id="element-4545" />
      <label className={`absolute left-4 transition-all duration-200 pointer-events-none ${isFocused || hasValue ? 'top-2 text-xs font-medium text-teal-600' : 'top-1/2 -translate-y-1/2 text-gray-500'}`} data-id="element-4546">
        {label}
        {required && <span className="text-rose-500 ml-0.5" data-id="element-4547">*</span>}
      </label>
      {icon && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-4548">
          {icon}
        </div>}
    </div>;
};
const PatientSelfRegisterPage: React.FC = () => {
  const {
    user,
    logout
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [existingPatient, setExistingPatient] = useState<any>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    date_of_birth: '',
    gender: '',
    identification_number: '',
    phone: '',
    address: '',
    city: '',
    state: ''
  });
  const [emergencyContact, setEmergencyContact] = useState({
    name: '',
    relationship: '',
    phone: ''
  });
  useEffect(() => {
    const checkExistingPatient = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }
      try {
        const {
          data,
          error: queryError
        } = await supabase.from('patients').select('*').eq('email', user.email).maybeSingle();
        if (queryError) {
          console.error('Error checking existing patient:', queryError);
          setLoading(false);
          return;
        }
        if (data) {
          setAlreadyRegistered(true);
          setExistingPatient(data);
        }
      } catch (err) {
        console.error('Error in checkExistingPatient:', err);
      } finally {
        setLoading(false);
      }
    };
    checkExistingPatient();
  }, [user?.email]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.date_of_birth || !formData.gender || !formData.phone) {
        setError('Por favor complete todos los campos obligatorios');
        setSubmitting(false);
        return;
      }
      const {
        data: insertedPatient,
        error: insertError
      } = await supabase.from('patients').insert([{
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender.toLowerCase(),
        identification_number: formData.identification_number || null,
        phone: formData.phone,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        emergency_contact: emergencyContact,
        created_at: new Date().toISOString()
      }]).select().single();
      if (insertError) {
        console.error('Supabase insert error:', insertError);
        setError(`Error al registrar: ${insertError.message}`);
        setSubmitting(false);
        return;
      }
      try {
        await supabase.from('audit_logs').insert([{
          user_id: user?.id || 'paciente-system',
          user_name: user?.name || 'Sistema de Registro de Pacientes',
          action: 'create',
          module: 'patients',
          entity_id: insertedPatient.id,
          entity_name: `${formData.first_name} ${formData.last_name}`,
          details: `Paciente registrado mediante auto-registro: ${formData.first_name} ${formData.last_name} (${formData.phone})`,
          created_at: new Date().toISOString()
        }]);
      } catch (auditError) {
        console.error('Error creating audit log:', auditError);
      }
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al registrar paciente');
    } finally {
      setSubmitting(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center" data-id="element-4549">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="text-center" data-id="element-4550">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" data-id="element-4551" />
          <p className="text-gray-600 font-medium" data-id="element-4552">Cargando...</p>
        </motion.div>
      </div>;
  }
  if (alreadyRegistered && existingPatient) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4" data-id="element-4553">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-teal-100" data-id="element-4554">
          <div className="text-center mb-6" data-id="element-4555">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4556">
              <CheckCircle className="w-10 h-10 text-white" data-id="element-4557" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4558">
              Ya Estás Registrado
            </h2>
            <p className="text-gray-600" data-id="element-4559">
              Tu información ya está en nuestro sistema
            </p>
          </div>

          <div className="bg-teal-50 rounded-xl p-6 mb-6 space-y-3" data-id="element-4560">
            <div className="flex items-center gap-3" data-id="element-4561">
              <User className="w-5 h-5 text-teal-600" data-id="element-4562" />
              <div data-id="element-4563">
                <p className="text-sm text-gray-600" data-id="element-4564">Nombre</p>
                <p className="font-semibold text-gray-900" data-id="element-4565">
                  {existingPatient.first_name || existingPatient.name}{' '}
                  {existingPatient.last_name || ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-4566">
              <Phone className="w-5 h-5 text-teal-600" data-id="element-4567" />
              <div data-id="element-4568">
                <p className="text-sm text-gray-600" data-id="element-4569">Teléfono</p>
                <p className="font-semibold text-gray-900" data-id="element-4570">
                  {existingPatient.phone}
                </p>
              </div>
            </div>
          </div>

          <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" data-id="element-4571">
            <LogOut className="w-5 h-5" data-id="element-4572" />
            Cerrar Sesión
          </button>
        </motion.div>
      </div>;
  }
  if (success) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4" data-id="element-4573">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-teal-100" data-id="element-4574">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200
        }} className="text-center mb-6" data-id="element-4575">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4576">
              <CheckCircle className="w-10 h-10 text-white" data-id="element-4577" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4578">
              ¡Registro Exitoso!
            </h2>
            <p className="text-gray-600" data-id="element-4579">
              Tu información ha sido guardada correctamente
            </p>
          </motion.div>

          <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" data-id="element-4580">
            <LogOut className="w-5 h-5" data-id="element-4581" />
            Cerrar Sesión
          </button>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50" data-id="element-4582">
      <motion.div initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8 px-4 shadow-lg" data-id="element-4583">
        <div className="max-w-4xl mx-auto" data-id="element-4584">
          <div className="flex items-center gap-4 mb-2" data-id="element-4585">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-4586">
              <UserCircle className="w-8 h-8" data-id="element-4587" />
            </div>
            <div data-id="element-4588">
              <h1 className="text-3xl font-bold" data-id="element-4589">Registro de Paciente</h1>
              <p className="text-teal-100 mt-1" data-id="element-4590">
                Complete su información personal
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-4591">
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} onSubmit={handleSubmit} className="space-y-6" data-id="element-4592">
          <AnimatePresence data-id="element-4593">
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center gap-3" data-id="element-4594">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-4595"></div>
                {error}
              </motion.div>}
          </AnimatePresence>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-4596">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 flex items-center gap-3" data-id="element-4597">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-4598">
                <User className="w-5 h-5 text-white" data-id="element-4599" />
              </div>
              <div data-id="element-4600">
                <h2 className="text-xl font-bold text-white" data-id="element-4601">
                  Información Personal
                </h2>
                <p className="text-teal-50 text-sm" data-id="element-4602">
                  Datos básicos del paciente
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4603">
              <FloatingLabelInput label="Nombre" value={formData.first_name} onChange={e => setFormData({
              ...formData,
              first_name: e.target.value
            })} required icon={<User className="w-5 h-5" data-id="element-4605" />} data-id="element-4604" />
              <FloatingLabelInput label="Apellido" value={formData.last_name} onChange={e => setFormData({
              ...formData,
              last_name: e.target.value
            })} required icon={<User className="w-5 h-5" data-id="element-4607" />} data-id="element-4606" />
              <FloatingLabelInput label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} required icon={<Mail className="w-5 h-5" data-id="element-4609" />} data-id="element-4608" />
              <FloatingLabelInput label="Fecha de Nacimiento" type="date" value={formData.date_of_birth} onChange={e => setFormData({
              ...formData,
              date_of_birth: e.target.value
            })} required icon={<Calendar className="w-5 h-5" data-id="element-4611" />} data-id="element-4610" />

              <div className="relative" data-id="element-4612">
                <select value={formData.gender} onChange={e => setFormData({
                ...formData,
                gender: e.target.value
              })} required className="w-full px-4 py-3.5 pt-6 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 transition-all duration-200 appearance-none bg-white outline-none" data-id="element-4613">
                  <option value="" data-id="element-4614">Seleccionar...</option>
                  <option value="masculino" data-id="element-4615">Masculino</option>
                  <option value="femenino" data-id="element-4616">Femenino</option>
                  <option value="otro" data-id="element-4617">Otro</option>
                </select>
                <label className="absolute left-4 top-2 text-xs font-medium text-teal-600 transition-all duration-200 pointer-events-none" data-id="element-4618">
                  Género<span className="text-rose-500 ml-0.5" data-id="element-4619">*</span>
                </label>
                <Users className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-4620" />
              </div>

              <FloatingLabelInput label="Número de Identificación" value={formData.identification_number} onChange={e => setFormData({
              ...formData,
              identification_number: e.target.value
            })} icon={<UserCircle className="w-5 h-5" data-id="element-4622" />} data-id="element-4621" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-4623">
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-6 py-4 flex items-center gap-3" data-id="element-4624">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-4625">
                <Phone className="w-5 h-5 text-white" data-id="element-4626" />
              </div>
              <div data-id="element-4627">
                <h2 className="text-xl font-bold text-white" data-id="element-4628">
                  Información de Contacto
                </h2>
                <p className="text-cyan-50 text-sm" data-id="element-4629">
                  Datos de ubicación y contacto
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4630">
              <FloatingLabelInput label="Teléfono" type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} required icon={<Phone className="w-5 h-5" data-id="element-4632" />} data-id="element-4631" />
              <FloatingLabelInput label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-4634" />} data-id="element-4633" />
              <FloatingLabelInput label="Ciudad" value={formData.city} onChange={e => setFormData({
              ...formData,
              city: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-4636" />} data-id="element-4635" />
              <FloatingLabelInput label="Estado/Provincia" value={formData.state} onChange={e => setFormData({
              ...formData,
              state: e.target.value
            })} icon={<MapPin className="w-5 h-5" data-id="element-4638" />} data-id="element-4637" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-4639">
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-6 py-4 flex items-center gap-3" data-id="element-4640">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center" data-id="element-4641">
                <Heart className="w-5 h-5 text-white" data-id="element-4642" />
              </div>
              <div data-id="element-4643">
                <h2 className="text-xl font-bold text-white" data-id="element-4644">
                  Contacto de Emergencia
                </h2>
                <p className="text-rose-50 text-sm" data-id="element-4645">
                  Persona a contactar en caso de emergencia
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4646">
              <FloatingLabelInput label="Nombre del Contacto" value={emergencyContact.name} onChange={e => setEmergencyContact({
              ...emergencyContact,
              name: e.target.value
            })} icon={<User className="w-5 h-5" data-id="element-4648" />} data-id="element-4647" />
              <FloatingLabelInput label="Relación" value={emergencyContact.relationship} onChange={e => setEmergencyContact({
              ...emergencyContact,
              relationship: e.target.value
            })} icon={<Users className="w-5 h-5" data-id="element-4650" />} data-id="element-4649" />
              <FloatingLabelInput label="Teléfono de Emergencia" type="tel" value={emergencyContact.phone} onChange={e => setEmergencyContact({
              ...emergencyContact,
              phone: e.target.value
            })} icon={<Phone className="w-5 h-5" data-id="element-4652" />} data-id="element-4651" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="flex gap-4" data-id="element-4653">
            <button type="submit" disabled={submitting} className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4654">
              {submitting ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-4655" />
                  Registrando...
                </> : <>
                  <CheckCircle className="w-5 h-5" data-id="element-4656" />
                  Registrar Paciente
                </>}
            </button>

            <button type="button" onClick={logout} className="px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center gap-2" data-id="element-4657">
              <LogOut className="w-5 h-5" data-id="element-4658" />
              Salir
            </button>
          </motion.div>
        </motion.form>
      </div>
    </div>;
};
export { PatientSelfRegisterPage };
export default PatientSelfRegisterPage;